import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  X,
  Lightbulb,
  SpeakerHigh,
  BookOpen,
  ArrowClockwise,
  Sparkle,
  Heart,
  CaretDown,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { BloomButton } from "../styleguide/ui/BloomButton";
import { Mascot } from "../styleguide/Mascot";
import { XPBadge } from "./rewards";

const LETTERS = ["A", "B", "C", "D"];

/* --------------------------- Multiple Choice Answer Card -------------------------- */
export function MultipleChoiceCard({
  prompt = "What is 7 × 6?",
  eyebrow = "Maths · Times tables",
  options = ["36", "42", "48", "54"],
  correctIndex = 1,
}) {
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | correct | wrong

  const check = () => {
    if (selected === null) return;
    setStatus(selected === correctIndex ? "correct" : "wrong");
  };
  const retry = () => setStatus("idle");
  const reset = () => { setSelected(null); setStatus("idle"); };

  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft" data-testid="mcq-card">
      <p className="text-xs font-bold uppercase tracking-wider text-primary-strong">{eyebrow}</p>
      <p className="mt-2 font-display text-2xl font-extrabold">{prompt}</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {options.map((opt, i) => {
          const isSel = selected === i;
          const isCorrect = i === correctIndex;
          const showCorrect = status === "correct" && isCorrect;
          const showWrong = status === "wrong" && isSel;
          return (
            <motion.button
              key={i}
              data-testid={`mcq-option-${i}`}
              onClick={() => status !== "correct" && (status === "wrong" ? (setSelected(i), setStatus("idle")) : setSelected(i))}
              animate={showWrong ? { x: [0, -8, 8, -5, 5, 0] } : { x: 0 }}
              transition={{ duration: 0.4 }}
              className={cn(
                "flex min-h-[60px] items-center gap-3 rounded-2xl border-2 px-4 text-left font-bold transition-colors",
                showCorrect && "border-teal bg-teal/15 text-teal-ink",
                showWrong && "border-destructive bg-destructive/10 text-destructive-ink",
                !showCorrect && !showWrong && isSel && "border-primary bg-primary/10 text-primary-strong",
                !showCorrect && !showWrong && !isSel && "border-border bg-card hover:border-primary/40",
              )}
            >
              <span className={cn(
                "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-sm font-extrabold",
                showCorrect ? "bg-teal text-teal-foreground" : showWrong ? "bg-destructive text-destructive-foreground" : isSel ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
              )}>
                {showCorrect ? <Check size={18} weight="bold" /> : showWrong ? <X size={18} weight="bold" /> : LETTERS[i]}
              </span>
              <span className="text-lg">{opt}</span>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {status !== "idle" && (
          <motion.div
            key={status}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={cn("mt-5 flex items-center gap-3 rounded-2xl p-4", status === "correct" ? "bg-teal/12" : "bg-primary/10")}
          >
            <Mascot size={56} mood={status === "correct" ? "celebrating" : "encouraging"} />
            <div className="flex-1">
              <p className={cn("font-display text-lg font-extrabold", status === "correct" ? "text-teal-ink" : "text-primary-strong")}>
                {status === "correct" ? "That's right — brilliant!" : "So close! Have another go."}
              </p>
              <p className="text-sm font-semibold text-muted-foreground">
                {status === "correct" ? "You earned 15 XP." : "Tip: try counting up in sixes."}
              </p>
            </div>
            {status === "correct" ? <XPBadge amount={15} /> : (
              <BloomButton size="sm" variant="soft" leftIcon={ArrowClockwise} onClick={retry} data-testid="mcq-retry">Try again</BloomButton>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-5 flex items-center justify-between">
      <div className="min-w-0 flex-1">
        <button onClick={reset} className="inline-flex min-h-[44px] items-center rounded-full px-3 text-sm font-bold text-muted-foreground hover:text-foreground">Reset</button>
      </div>
        <BloomButton disabled={selected === null || status === "correct"} onClick={check} rightIcon={Check} data-testid="mcq-check">Check answer</BloomButton>
      </div>
    </div>
  );
}

/* ------------------------------ Reading Passage Card ----------------------------- */
export function ReadingPassageCard() {
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-teal/15 px-3 py-1.5 text-xs font-bold text-teal-ink">
          <BookOpen size={14} weight="fill" /> Reading
        </span>
        <button className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-2 text-sm font-bold text-foreground hover:bg-primary/10 hover:text-primary-strong">
          <SpeakerHigh size={16} weight="fill" /> Read aloud
        </button>
      </div>
      <h4 className="mt-4 font-display text-xl font-extrabold">The Little Seed</h4>
      <p className="mt-2 text-lg font-medium leading-relaxed text-foreground">
        Deep in the warm soil, a tiny seed began to stir. Each morning the sun gave it light, and each evening the
        rain gave it a drink. Slowly, slowly, a small green shoot reached up toward the sky.
      </p>
      <div className="mt-4 rounded-2xl bg-muted/50 p-4">
        <p className="text-sm font-bold text-primary-strong">Question</p>
        <p className="font-semibold">What helped the seed to grow?</p>
      </div>
    </div>
  );
}

/* ----------------------------------- Hint Card ----------------------------------- */
export function HintCard({ hint = "Remember: 7 × 6 is the same as 6 added together 7 times." }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={cn("rounded-3xl border-2 bg-card p-5 shadow-soft transition-all duration-300", open ? "border-secondary ring-4 ring-secondary/20" : "border-border")}>
      <button onClick={() => setOpen((o) => !o)} data-testid="hint-toggle" className="flex w-full items-center gap-3 text-left">
        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-secondary/25 text-secondary-ink">
          <Lightbulb size={22} weight="fill" />
        </span>
        <span className="flex-1 font-display text-lg font-extrabold">Need a hint?</span>
        <CaretDown size={20} weight="bold" className={cn("text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: "auto", opacity: 1, marginTop: 12 }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            className="overflow-hidden pl-14 font-medium leading-relaxed text-muted-foreground"
          >
            {hint}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

/* -------------------------------- Explanation Card ------------------------------- */
export function ExplanationCard() {
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <div className="flex items-center gap-2 text-blue-ink">
        <Sparkle size={20} weight="fill" />
        <p className="font-display text-lg font-extrabold">Let&apos;s see why</p>
      </div>
      <p className="mt-2 font-medium leading-relaxed text-foreground">
        7 × 6 means six, seven times. We can add in steps:
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {["6", "12", "18", "24", "30", "36", "42"].map((n, i) => (
          <span key={i} className={cn("flex h-10 min-w-[2.5rem] items-center justify-center rounded-xl px-2 font-bold", i === 6 ? "bg-teal/15 text-teal-ink" : "bg-muted text-foreground")}>
            {n}
          </span>
        ))}
      </div>
      <p className="mt-3 font-semibold text-muted-foreground">So 7 × 6 = <strong className="text-teal-ink">42</strong>. Well done for sticking with it!</p>
    </div>
  );
}

/* -------------------------------- Celebration Card ------------------------------- */
export function CelebrationCard() {
  const confetti = [
    { l: "12%", c: "bg-primary", d: "0s" }, { l: "28%", c: "bg-teal", d: ".1s" },
    { l: "48%", c: "bg-secondary", d: ".05s" }, { l: "68%", c: "bg-blue", d: ".15s" },
    { l: "84%", c: "bg-primary", d: ".08s" },
  ];
  return (
    <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-b from-accent/70 to-card p-8 text-center shadow-soft">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {confetti.map((c, i) => (
          <span key={i} className={cn("absolute top-2 h-3 w-3 rounded-sm motion-safe:animate-float", c.c)} style={{ left: c.l, animationDelay: c.d }} />
        ))}
      </div>
      <div className="relative flex flex-col items-center">
        <Mascot size={120} mood="celebrating" />
        <p className="mt-3 font-display text-2xl font-black">Lesson complete!</p>
        <p className="mt-1 font-semibold text-muted-foreground">You answered every question. Amazing effort!</p>
        <div className="mt-4 flex items-center gap-2">
          <XPBadge amount={50} />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-teal/15 px-3 py-1.5 text-sm font-bold text-teal-ink">
            <Heart size={15} weight="fill" /> Confidence +1
          </span>
        </div>
        <BloomButton className="mt-5" rightIcon={Sparkle}>Continue</BloomButton>
      </div>
    </div>
  );
}
